import { darken, Popover, Stack } from "@mui/material"
import { GridActionsCellItem, GridRowEditStopReasons, GridRowModes, useGridApiRef } from "@mui/x-data-grid"
import { FieldArray, useFormikContext } from "formik"
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react"
import logger from "../../../../../Logger"
import { UiCtDataGrid } from "../../../MuiStyled/LibComponent"
import { UiFieldErrorText } from "../../../MuiStyled/Text"
import { FIELD_TYPES, tableGroupValueIdKey } from "../../dynamic-form.constants"
import { getInNested } from "../../dynamic-form.helper"
import AddRowOverlay from "./components/addRowOverlay"
import CustomEditComponent from "./components/customEdit"
import EditToolbar from "./components/editToolbar"

const pageLogger = logger.child({ module: "Dynamic Form | Field Table" })
const actionsColumnName = "__actions"

function FieldTable(props) {
  const { id, name, table_data, table_schema, fieldData, groupLabel, isExportEnabled, isImportEnabled, shadowFormNodeId, isMutable } = props
  // Contextual empty-state copy derived from the section heading (display only).
  const emptyLabel = groupLabel ? `No ${groupLabel.toLowerCase()} yet` : "No rows yet"
  const { values, errors, touched, submitCount, setFieldValue } = useFormikContext()
  const rowModesModel = useRef({})
  const gridContainerRef = useRef(null)
  // States related to popover
  const [anchorEle, setAnchorEle] = useState(null)
  const [popoverText, setPopOverText] = useState("")
  const open = Boolean(anchorEle)

  const dataGridAPI = useGridApiRef()

  // Force override inline min-height styles on virtual scroller elements (but preserve height for visibility)
  useEffect(() => {
    const overrideMinHeight = () => {
      if (gridContainerRef.current) {
        const virtualScrollerContent = gridContainerRef.current.querySelector(".MuiDataGrid-virtualScrollerContent")
        const virtualScroller = gridContainerRef.current.querySelector(".MuiDataGrid-virtualScroller")

        // Only override min-height, not height (to preserve row visibility)
        if (virtualScrollerContent) {
          virtualScrollerContent.style.setProperty("min-height", "auto", "important")
        }
        if (virtualScroller) {
          virtualScroller.style.setProperty("min-height", "auto", "important")
        }
      }
    }

    // Run immediately and after a short delay to catch initial render
    overrideMinHeight()
    const timeoutId = setTimeout(overrideMinHeight, 100)

    // Use MutationObserver to watch for style attribute changes
    if (gridContainerRef.current) {
      const observer = new MutationObserver((mutations) => {
        // Only re-apply if min-height was changed
        const shouldOverride = mutations.some(
          (mutation) =>
            mutation.type === "attributes" &&
            mutation.attributeName === "style" &&
            mutation.target instanceof HTMLElement &&
            (mutation.target.classList.contains("MuiDataGrid-virtualScrollerContent") ||
              mutation.target.classList.contains("MuiDataGrid-virtualScroller"))
        )
        if (shouldOverride) {
          overrideMinHeight()
        }
      })

      observer.observe(gridContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      })

      return () => {
        clearTimeout(timeoutId)
        observer.disconnect()
      }
    }
  }, [table_data.length])

  const handlePopoverClose = () => {
    setAnchorEle(null)
  }

  const getRowId = (row) => row[tableGroupValueIdKey]

  const getRowIndex = useCallback(
    (id) => {
      return table_data.findIndex((row) => row[tableGroupValueIdKey] === id) ?? -1
    },
    [table_data]
  )

  const getRowHeight = ({ id }) => {
    return "auto"
  }

  const getRowSpacing = (params) => ({
    top: 2,
    bottom: params.isLastVisible ? 5 : 0,
    end: 10,
  })

  const handlePopoverOpen = useCallback(
    (event) => {
      const id = event.currentTarget.parentElement.dataset.id
      const field = event.currentTarget.dataset.field
      const isInEditMode = rowModesModel?.current?.[id]?.mode === GridRowModes.Edit
      const rowIndex = getRowIndex(id)
      const errorText = errors?.[name]?.[rowIndex]?.[field] ? errors[name][rowIndex][field] : ""

      if (!isInEditMode && errorText) {
        setPopOverText(errors?.[name]?.[rowIndex]?.[field] ? errors[name][rowIndex][field] : "")
        setAnchorEle(event.currentTarget)
      }
    },
    [getRowIndex, errors]
  )

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const handleEditClick = ({ id, arrayHelpers }) => {
    dataGridAPI.current.startRowEditMode({ id })
  }

  const handleDeleteClick = useCallback(
    ({ id, arrayHelpers }) => {
      const rowIndex = getRowIndex(id)
      if (rowIndex === -1) {
        return // abort
      }
      arrayHelpers.remove(rowIndex)
      pageLogger.info({ id, rowIndex }, `Deleted row...`)
    },
    [getRowIndex]
  )

  const handleInsertRowAfterId = useCallback(
    ({ id, arrayHelpers }) => {
      // Find index of current id
      const insertIndex = getRowIndex(id)
      if (insertIndex === -1) {
        return // abort
      }
      pageLogger.info({ id, insertAt: insertIndex + 1 }, `Adding row...`)
      arrayHelpers.insert(insertIndex + 1, table_schema)
    },
    [table_schema, getRowIndex]
  )

  const handleInsertRowAtBeginning = useCallback(
    ({ arrayHelpers }) => {
      pageLogger.info(`Inserting new row at index 0...`)
      // Add Formik data field
      arrayHelpers.unshift(table_schema)
    },
    [table_schema]
  )

  const handleDismissEdit = ({ id, arrayHelpers, shouldSave = false }) => {
    if (shouldSave) {
      dataGridAPI.current.stopRowEditMode({ id })
    } else {
      dataGridAPI.current.stopRowEditMode({ id, ignoreModifications: true })
    }
  }

  const handleRowUpdate = useCallback(
    ({ newRow, arrayHelpers }) => {
      const newRowId = newRow?.[tableGroupValueIdKey]
      const rowIndex = getRowIndex(newRowId)
      if (rowIndex === -1) {
        return // abort
      }
      const newRowValue = { ...newRow, [actionsColumnName]: undefined } // mask away special actions column values
      pageLogger.info({ newRowValue }, `Row updated`)

      arrayHelpers.replace(rowIndex, newRowValue)

      return newRow
    },
    [getRowIndex]
  )

  const handleRowModesModelChange = (newRowModesState) => {
    rowModesModel.current = newRowModesState
  }

  const getColumnConfig = useCallback(
    (arrayHelpers) => {
      const columnConfig = fieldData
        .filter((f) => f.type !== FIELD_TYPES.ACTION_BTN && f.enabled !== false && f.width !== 0)
        .map((f) => {
          if (f.type === FIELD_TYPES.FACILITATOR_TOGGLE) {
            return {
              field: f.name,
              headerName: f.label,
              align: "center",
              headerAlign: "center",
              disableColumnMenu: true,
              sortable: false,
              filterable: false,
              editable: false,
              flex: f.width ?? 1,
              renderCell: (params) => {
                const checked = !!params.row[f.name]
                return (
                  <input
                    type="checkbox"
                    checked={checked}
                    style={{ accentColor: "#F6AB36", width: 16, height: 16, cursor: "pointer" }}
                    onChange={() => {
                      // Facilitators are CO-EQUAL: toggle only the clicked row, leave the others
                      // unchanged (checkbox semantics, matching RosterTable). The prior version set
                      // every other row to false, forcing single-select.
                      const rows = getInNested(values, name) ?? []
                      const updated = rows.map((row) =>
                        row[tableGroupValueIdKey] === params.id ? { ...row, [f.name]: !checked } : row
                      )
                      setFieldValue(name, updated)
                    }}
                  />
                )
              },
            }
          }
          return {
            field: f.name,
            headerName: f.label,
            align: "left",
            disableColumnMenu: true,
            sortable: false,
            filterable: false,
            editable: f.enabled ?? true,
            flex: f.width ?? 1,
            renderEditCell: (params) => (
              <CustomEditComponent
                {...params}
                key={`editCell_${params.id}`}
                rowIndex={getRowIndex(params.id)}
                parentProps={props}
                fieldName={f.name}
                shadowFormNodeId={shadowFormNodeId}
                onCellLayout={handlePopoverClose}
                values={values}
                errors={errors}
              />
            ),
          }
        })

      columnConfig.push({
        field: actionsColumnName,
        headerName: "",
        width: 100,
        sortable: false,
        editable: true,
        filterable: false,
        disableExport: true,
        renderCell: (params) => (
          <Stack direction={"row"}>
            <GridActionsCellItem
              key={`action-cell-${params.id}-03`}
              tabIndex={0}
              icon={<i className="fa-solid fa-pencil"></i>}
              label="Edit"
              className="textPrimary"
              onClick={() => handleEditClick({ id: params.id, arrayHelpers })}
              color="inherit"
            />
            {isMutable ? (
              <GridActionsCellItem
                key={`action-cell-${params.id}-04`}
                tabIndex={1}
                icon={<i className="fa-regular fa-trash-can"></i>}
                label="Delete"
                onClick={() => handleDeleteClick({ id: params.id, arrayHelpers })}
                color="inherit"
              />
            ) : (
              <></>
            )}
            {isMutable ? (
              <GridActionsCellItem
                key={`action-cell-${params.id}-05`}
                tabIndex={2}
                icon={<i className="fa-regular fa-plus"></i>}
                label="Insert"
                onClick={() => handleInsertRowAfterId({ id: params.id, arrayHelpers })}
                color="inherit"
              />
            ) : (
              <></>
            )}
          </Stack>
        ),
        renderEditCell: (params) => (
          <Stack direction={"row"} sx={{ paddingLeft: "10px" }} tabIndex={0}>
            <GridActionsCellItem
              key={`action-cell-${params.id}-01`}
              tabIndex={1}
              icon={<i className="fa-regular fa-floppy-disk"></i>}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={() => handleDismissEdit({ id: params.id, arrayHelpers, shouldSave: true })}
            />
            <GridActionsCellItem
              key={`action-cell-${params.id}-02`}
              tabIndex={2}
              icon={<i className="fa-solid fa-xmark"></i>}
              label="Cancel"
              className="textPrimary"
              onClick={() => handleDismissEdit({ id: params.id, arrayHelpers })}
              color="inherit"
            />
          </Stack>
        ),
      })

      return columnConfig
    },
    [fieldData, values, errors, shadowFormNodeId, handleInsertRowAfterId, handleDeleteClick]
  )

  const getRowClassNames = useCallback(
    (id) => {
      let classNames = []

      if (rowModesModel?.current?.[id]?.mode === GridRowModes.Edit) {
        classNames.push("row-edit")
      } else if (errors[name]) {
        const rowIndex = getRowIndex(id)
        classNames.push(errors[name][rowIndex] ? "row-invalid" : "")
      }

      return classNames.join(" ")
    },
    [errors, name, getRowIndex]
  )

  const handleImportContent = ({ importContent, arrayHelpers, postErrorMessage, postSuccessMessage }) => {
    try {
      // Quickly validate that the data shape is an array of objects and the keys match the group fields
      if (!Array.isArray(importContent)) {
        postErrorMessage(`Data doesn't have the correct shape: List of Objects`)
        return
      }

      pageLogger.info({ importContent }, `Importing CSV to table`)

      const allFields = fieldData.filter((f) => f.type !== FIELD_TYPES.ACTION_BTN)

      const validItems = []

      importContent.forEach((anImportItem, rowIdx) => {
        // Loop through the table columns and verify against the object keys
        // 1. must match a field "Label"
        // 2. Or, must must the field "name"
        const importItemEntries = Object.entries(anImportItem)

        const importItem = {}

        for (const f of allFields) {
          for (const [aKey, aValue] of importItemEntries) {
            if (aKey === f.label || aKey === f.name) {
              // Found value for field
              if (Array.isArray(aValue)) {
                importItem[f.name] = aValue.join(",")
              } else {
                importItem[f.name] = `${aValue}`
              }
              break // inner for loop, done with this field
            }
          }
        }

        const itemKeyCount = Object.keys(importItem)?.length ?? 0
        if (itemKeyCount > 0) {
          validItems.push(importItem)
        }
      })

      if (validItems.length > 0) {
        // Pop all values from table
        while (true) {
          const poppedVal = arrayHelpers.pop()
          if (!poppedVal) {
            break // while
          }
        }
        // Push all new values into table
        for (const newItem of validItems) {
          arrayHelpers.push(newItem)
        }
        postSuccessMessage(`Imported ${validItems.length} rows!`)
      } else {
        postErrorMessage(
          `No valid rows found in CSV! Check that the column names are in this list: ${allFields
            .map((f) => `"` + f.label + `"`)
            .concat(allFields.map((f) => `"` + f.name + `"`))
            .join(", ")}`
        )
      }
    } catch (error) {
      postErrorMessage(`${error}`)
    }
  }

  return (
    <FieldArray
      id={id}
      name={name}
      render={(arrayHelpers) => {
        const columnConfig = getColumnConfig(arrayHelpers)
        const groupError = getInNested(errors, name, false)
        // Only surface the group-level error (e.g. "At least one player…") after the
        // user has interacted with this group OR attempted to advance — never on the
        // first render. Field-level errors are already touch-gated in each input.
        const groupErrorTouched = !!getInNested(touched, name, false) || submitCount > 0
        const groupErrorMessage = groupErrorTouched && groupError && typeof groupError === "string" ? groupError : ""
        return (
          columnConfig && (
            <Fragment>
              <div className="col-12 custom-form-block d-flex flex-column" ref={gridContainerRef}>
                <UiCtDataGrid
                  // initialState={{
                  //   density: "compact",
                  // }}
                  apiRef={dataGridAPI}
                  className="custom__data-grid"
                  columns={columnConfig}
                  rows={table_data}
                  getRowId={getRowId}
                  editMode="row"
                  // rowModesModel={rowModesModel}
                  onRowModesModelChange={handleRowModesModelChange}
                  onRowEditStop={handleRowEditStop}
                  processRowUpdate={(newRow) => handleRowUpdate({ newRow, arrayHelpers })}
                  disableRowSelectionOnClick
                  getRowHeight={getRowHeight}
                  getEstimatedRowHeight={() => 40}
                  rowHeight={40}
                  getRowSpacing={getRowSpacing}
                  getRowClassName={({ id }) => getRowClassNames(id)}
                  hideFooter={true}
                  slots={{
                    toolbar: EditToolbar,
                    noRowsOverlay: AddRowOverlay,
                  }}
                  slotProps={{
                    toolbar: { name, table_schema, isExportEnabled, isImportEnabled, isMutable, arrayHelpers, handleImportContent },
                    noRowsOverlay: { handleAddRow: isMutable ? () => handleInsertRowAtBeginning({ arrayHelpers }) : null, emptyLabel },
                    cell: {
                      onMouseEnter: handlePopoverOpen,
                      onMouseLeave: handlePopoverClose,
                    },
                  }}
                  scrollbarSize={0}
                  sx={{
                    height: table_data.length === 0 ? "auto" : undefined,
                    "& .MuiDataGrid-overlayWrapper": {
                      height: "auto !important",
                      minHeight: "auto !important",
                      padding: table_data.length === 0 ? "0" : undefined,
                    },
                    "& .MuiDataGrid-overlayWrapperInner": {
                      height: "auto !important",
                      minHeight: "auto !important",
                    },
                    "& .MuiDataGrid-main": {
                      minHeight: table_data.length === 0 ? "auto" : undefined,
                    },
                    "& .MuiDataGrid-root": {
                      minHeight: table_data.length === 0 ? "auto" : undefined,
                    },
                    "& .MuiDataGrid-virtualScrollerContent": {
                      minHeight: "auto !important",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      minHeight: "auto !important",
                    },
                    "& .MuiDataGrid-virtualScrollerRenderZone": {
                      minHeight: "auto !important",
                    },
                    "& .MuiDataGrid-row.row-invalid": {
                      background: () => {
                        return darken("#F50004", 0.8)
                      },
                    },
                    "& .MuiDataGrid-row--editing": {
                      "& .MuiDataGrid-cell": {
                        background: () => {
                          return darken("#F6AB36", 0.8)
                        },
                      },
                    },
                    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                  }}
                />
                <div className="invalid-feedback">{groupErrorMessage}</div>
                {/* POPOVER */}
                <Popover
                  sx={{
                    pointerEvents: "none",
                  }}
                  open={open}
                  anchorEl={anchorEle}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  onClose={handlePopoverClose}
                  disableRestoreFocus
                >
                  <UiFieldErrorText>{popoverText}</UiFieldErrorText>
                </Popover>
              </div>
            </Fragment>
          )
        )
      }}
    />
  )
}

export default FieldTable
